import React, { useState, useEffect } from "react";
import Typography from "@material-ui/core/Typography";
import { useTranslation } from "react-i18next";
import CommentForm from "./CommentForm";
import CommentList from "./CommentList";
import { getComments, saveComment } from "../../firebase/comments";
import { Comment } from "../../firebase/comments";
import {
  getCommentsFlaggedByUser,
  flagComment as flagCommentInDatabase,
  unflagComment as unflagCommentInDatabase,
} from "../../firebase/commentflags";
import { useAuth } from "../../firebase/AuthProvider";
import { Statement } from "../../firebase/statements";
import { Grid, Hidden } from "@material-ui/core";
import InfoIcon from '@material-ui/icons/Info';
import { colors } from "../../theme";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: "32px",
    paddingTop: "50px",
    borderTop: "1px solid " + colors.COMMENT_BORDER,
  },
  icon: {
    paddingLeft: "15px",
    marginTop: "-3px",    
    "&:hover": {
      cursor: "pointer"
    }
  },
  infoText: {
    position: "relative",
    backgroundColor: "#FFF",
    padding: "15px",
    border: "1px solid",
    borderColor: colors.BORDER,
    borderRadius: "10px",
    zIndex: 2
  },
  subtitle: {
    color: colors.TEXT_PRIMARY,
    fontSize: "20px",
    fontWeight: 500,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "-0.05px",
  },
}));

interface Props {
  statement: Statement;
}

export default function Comments({ statement }: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isShown, setIsShown] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsFlaggedByUser, setCommentsFlaggedByUser] = useState<string[]>(
    []
  );
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    const unsubscribe = getComments(statement.id, setComments);
    return () => unsubscribe();
  }, [statement]);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = getCommentsFlaggedByUser(
      statement.id,
      user.id,
      setCommentsFlaggedByUser
    );
    return () => unsubscribe();
  }, [statement, user]);

  function createComment(comment: Comment) {
    if (!user) return;
    saveComment(statement, comment, user);
  }

  async function flagComment(comment: Comment, flagged: boolean) {
    if (!user) return;

    flagged
      ? await flagCommentInDatabase(comment, user)
      : await unflagCommentInDatabase(comment.id, user.id, isAdmin);
  }

  return (
    <Grid container item xs={12}>
      <Hidden xsDown>
        <Grid container item sm={3} />
      </Hidden>
      <Grid container item xs={12} sm={6} className={classes.container}>
        <Hidden smUp>
          <Grid container item xs={1} />
        </Hidden>
        <Grid container item xs={10}>
          <Typography 
          component="h2" 
          className={classes.subtitle}>
          {t("commentform.whatdoyouthink")}
          </Typography>

          <span className={classes.icon}>
            <InfoIcon 
            style={{color: colors.BUTTON_BG }}
            onMouseEnter={() => setIsShown(true)}
            onMouseLeave={() => setIsShown(false)} />
          </span>

          {isShown && (
            <Typography className={classes.infoText} component="p">
              {t("commentform.helptext")}
            </Typography>
          )}
          <Hidden smUp>
            <Grid container item xs={1} />
          </Hidden>
        </Grid>

        <Grid container item xs={12}>
          <Grid container item xs={1} />
          <Grid container item xs={10}>
            <CommentForm save={createComment} />
          </Grid>
          <Grid container item xs={1} />
        </Grid>

        <Grid container item xs={12}>
          <Grid container item xs={1} />
          <Grid container item xs={10}>
            <CommentList
              comments={comments}
              flagComment={flagComment}
              commentsFlaggedByUser={commentsFlaggedByUser}
            />
          </Grid>
          <Grid container item xs={1} />
        </Grid>
      </Grid>
      <Hidden xsDown>
        <Grid container item sm={3} />
      </Hidden>
    </Grid>
  );
}
